export const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T => {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`Missing element: ${sel}`);
  return el;
};
export const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] => [
  ...root.querySelectorAll<T>(sel),
];

/** SVG sprite icon markup (static ids only — never interpolate user text here). */
export const ic = (id: string) => `<svg class="ic" aria-hidden="true"><use href="#i-${id}"/></svg>`;

const NS = 'http://www.w3.org/2000/svg';
export function svgEl<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, name);
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  return e;
}

export const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Element.animate that is skipped under prefers-reduced-motion. */
export function animate(el: Element, frames: Keyframe[], opts: KeyframeAnimationOptions) {
  if (reducedMotion() || typeof el.animate !== 'function') return null;
  return el.animate(frames, opts);
}

/** Press-and-hold repeater for −/+ buttons (single taps and keyboard activation work too). */
export function holdable(btn: HTMLElement, fn: () => void) {
  let delay: ReturnType<typeof setTimeout> | undefined;
  let rep: ReturnType<typeof setInterval> | undefined;
  let held = false;
  btn.addEventListener('pointerdown', (e) => {
    if (e.button) return;
    held = false;
    delay = setTimeout(() => {
      held = true;
      rep = setInterval(fn, 90);
    }, 450);
  });
  const stop = () => {
    clearTimeout(delay);
    clearInterval(rep);
  };
  for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) btn.addEventListener(ev, stop);
  btn.addEventListener('click', () => {
    stop();
    if (held) {
      held = false;
      return;
    }
    fn();
  });
}

/** Two-step confirm for destructive buttons. */
export function armConfirm(btn: HTMLButtonElement, fn: () => void, prompt = 'TAP AGAIN TO CONFIRM') {
  const label = btn.textContent ?? '';
  let timer: ReturnType<typeof setTimeout> | undefined;
  btn.addEventListener('click', () => {
    if (btn.dataset.armed) {
      delete btn.dataset.armed;
      clearTimeout(timer);
      btn.textContent = label;
      fn();
      return;
    }
    btn.dataset.armed = '1';
    btn.textContent = prompt;
    timer = setTimeout(() => {
      delete btn.dataset.armed;
      btn.textContent = label;
    }, 2600);
  });
}

export function download(name: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
